import { LocationVarKey } from "../Generated/LocationVarKey.Data";
import { URLKey } from "./CortexURLs.Data";

/**
 * The ONE feature vocabulary for units and facilities (ENG-3209).
 *
 * Before this file, the same real-world thing ("this space is climate
 * controlled") lived in two unrelated enums: the legacy per-unit `SpaceFeature`
 * (written by the FMS adapters into `unit.features`) and the Snowy per-location
 * `FacilityFeatureKey` (one operator switch per key). They used different values
 * and different naming, and the bot bridged them by substring matching.
 *
 * Now every entry is one member of `FacilityAndSpaceFeatures` with a `scope`:
 *
 * - `unit`     — a tag FMS sync writes onto a unit row. Operators never toggle
 *                it; they can only rename it.
 * - `facility` — an operator switch describing the location. FMS never writes it.
 * - `both`     — an operator switch that FMS also writes on units.
 *
 * VALUES ARE THE LEGACY `SpaceFeature` STRINGS, byte-for-byte, lowercase with
 * spaces. `unit.features` holds live production data for every synced unit, so
 * those strings never change. The Snowy-side keys (pre-launch) took the change.
 *
 * A feature is up to three ORDINARY location variables, one per property, each
 * inheriting org → group → location on its own like every other variable:
 *   - `cortexVarKey`  on/off, "0"/"1"       — operator switches only (facility / both)
 *   - `nameVarKey`    display-name override — EVERY entry; empty means the catalog label
 *   - `botWordingVarKey`  custom wording        — operator switches only; empty means the
 *                                             catalog on/off default text
 * Nothing is serialized: a feature's name is a text variable a text input, a template,
 * or the bot can read like any other. (This replaces the June 2026 "single serialized
 * unit" storage — see the ENG-3209 plan for why.) Unit tags have no on/off: the FMS
 * decides which units carry them; the operator only names them.
 *
 * Compound facility switches (e.g. `covered rv parking`) declare `composedOf`:
 * a unit tagged `rv parking` + `covered` deterministically matches the compound.
 * That replaces the old `fmsHints` substring bridge.
 *
 * Legacy surfaces (FMS adapters, bull sync, the legacy chat space picker,
 * messaging handlers, studio lease card) reference this enum by identifier only
 * and keep their own display map (`DefaultSpacePickerFeatureDisplayNames`).
 *
 * See plan: docs/snowy/plans/2026-09-04-facility-and-space-features-enum-merge.md
 */

export type FeatureScope = "facility" | "unit" | "both";
/** `unit_attribute` = a unit-only tag (never an operator switch); the other three are the PM page's cards. */
export type FeatureCategory = "amenity" | "service" | "storage_type" | "unit_attribute";

/**
 * How a compound facility feature is made of unit tags. A unit matches when it
 * carries every tag in `all` and, if `any` is present, at least one tag in `any`.
 */
export interface FeatureComposition {
  all: FacilityAndSpaceFeatures[];
  any?: FacilityAndSpaceFeatures[];
}

export interface FacilityAndSpaceFeatureDef {
  /** Stable enum key — the legacy `SpaceFeature` string. Never changes. */
  key: FacilityAndSpaceFeatures;
  /** Human label: PM row name, bot prose, widget chip. Overridable per location via the unit's `nameOverride`. */
  label: string;
  /** Where the feature applies — see the file header. */
  scope: FeatureScope;
  /** PM card grouping + the bot's amenities/services/storageTypes split. `unit_attribute` never appears as a switch. */
  category: FeatureCategory;
  /** Short prose used when the bot describes the feature individually. */
  description: string;
  /** On/off switch, "0"/"1". Operator switches only — absent for unit tags (the FMS decides). */
  cortexVarKey?: LocationVarKey;
  /** Operator display-name override, plain text. Every entry. Empty = use `label`. */
  nameVarKey: LocationVarKey;
  /** Operator custom wording the bot reads, plain text. Switches only. Empty = catalog on/off default. */
  botWordingVarKey?: LocationVarKey;
  /** Unit tags that together mean this compound facility feature. Compounds only. */
  composedOf?: FeatureComposition;
  /** Legacy `Has*` flag the one-shot migration seeds `on` from. Absent for unit-only tags (nothing legacy to seed). */
  legacyHasKey?: LocationVarKey;
  /** Legacy var holding the operator's display-name override; migrated into the unit's `nameOverride`. */
  legacyNameKey?: LocationVarKey;
  /** Legacy var holding the operator's custom text; migrated into the unit's `customText`. */
  legacyTextKey?: LocationVarKey;
  /** Optional — pairs this feature with a URL. */
  pairedUrlKey?: URLKey;
}

export enum FacilityAndSpaceFeatures {
  // ─── Unit tags (scope "unit") — written by FMS sync, never operator-toggled ──
  StorageUnit = "storage unit",
  FirstFloor = "first floor",
  UpperLevel = "upper level",
  Inside = "inside",
  Paved = "paved",
  Unpaved = "unpaved",
  Covered = "covered",
  Enclosed = "enclosed",
  PullThru = "pull thru",
  Alarm = "alarm",
  LightInUnit = "light in unit",
  HighCeiling = "high ceiling",
  Obstruction = "obstruction",
  OddShaped = "odd shaped",
  RollDoor = "roll door",
  SmallRollDoor = "small roll door",
  LargeRollDoor = "large roll door",
  DoubleDoor = "double door",
  SwingDoor = "swing door",
  GunStorage = "gun storage",
  SafetyDepositBox = "safety deposit box",
  RinseStation = "rinse station",
  AirAndVacuumStation = "air and vacuum station",

  // ─── Both levels (scope "both") — FMS writes on units, operators toggle for the facility ──
  ClimateControl = "climate control",
  Heated = "heated",
  DriveUp = "drive up",
  Elevators = "elevator access",
  Power = "power",
  Locker = "locker",
  OfficeSpace = "office space",
  Parking = "parking",
  RVParking = "rv parking",
  BoatParking = "boat parking",
  WineStorage = "wine storage",
  CommercialWarehouse = "commercial warehouse",
  ContainerUnit = "container unit",
  TwentyFourHourAccess = "24 hour access",
  RinseAndDumpStation = "rinse and dump station",

  // ─── Facility compounds (scope "facility") — operator switches with `composedOf` ──
  CoveredRVParking = "covered rv parking",
  EnclosedRVParking = "enclosed rv parking",
  CoveredVehicleStorage = "covered vehicle storage",
  IndoorVehicleStorage = "indoor vehicle storage",
  SmallVehicleStorage = "small vehicle storage",

  // ─── Facility-only (scope "facility") — operator switches FMS never writes ──
  AfterHoursAccess = "after hours access",
  HumidityControl = "humidity control",
  VideoSurveillance = "video surveillance",
  ConferenceRoom = "conference room",
  OnSiteManager = "on site manager",
  POMailbox = "po mailbox",
  Notary = "notary",
  DeliveriesAccepted = "deliveries accepted",
  Dumpsters = "dumpsters",
  MovingTrucks = "moving trucks",
  MovingServices = "moving services",
  PackingSupplies = "packing supplies",
  Dollies = "dollies",
  Padlocks = "padlocks",
  Propane = "propane",
  RecycleProgram = "recycle program",
  ShredProgram = "shred program",
  Appointments = "appointments",
  TrailerStorage = "trailer storage"
}

/** A unit-only tag def: no switch, no wording, no legacy flag — just a name the operator can override. */
function unitTag(key: FacilityAndSpaceFeatures, label: string, description: string, nameVarKey: LocationVarKey): FacilityAndSpaceFeatureDef {
  return { key, label, scope: "unit", category: "unit_attribute", description, nameVarKey };
}

export const FACILITY_AND_SPACE_FEATURES: readonly FacilityAndSpaceFeatureDef[] = [
  // ─── Unit tags ─────────────────────────────────────────────────
  unitTag(FacilityAndSpaceFeatures.StorageUnit, "Storage Unit", "A standard self-storage unit.", LocationVarKey.CortexFeatureStorageUnitName),
  unitTag(FacilityAndSpaceFeatures.FirstFloor, "First Floor", "The unit is on the ground floor.", LocationVarKey.CortexFeatureFirstFloorName),
  unitTag(FacilityAndSpaceFeatures.UpperLevel, "Upper Level", "The unit is on an upper floor.", LocationVarKey.CortexFeatureUpperLevelName),
  unitTag(FacilityAndSpaceFeatures.Inside, "Inside", "The unit is inside the building.", LocationVarKey.CortexFeatureInsideName),
  unitTag(FacilityAndSpaceFeatures.Paved, "Paved", "The space is on a paved surface.", LocationVarKey.CortexFeaturePavedName),
  unitTag(FacilityAndSpaceFeatures.Unpaved, "Unpaved", "The space is on an unpaved surface.", LocationVarKey.CortexFeatureUnpavedName),
  unitTag(FacilityAndSpaceFeatures.Covered, "Covered", "The space has a roof but is open on the sides.", LocationVarKey.CortexFeatureCoveredName),
  unitTag(FacilityAndSpaceFeatures.Enclosed, "Enclosed", "The space is fully enclosed.", LocationVarKey.CortexFeatureEnclosedName),
  unitTag(
    FacilityAndSpaceFeatures.PullThru,
    "Pull Thru",
    "The space can be driven through rather than backed into.",
    LocationVarKey.CortexFeaturePullThruName
  ),
  unitTag(FacilityAndSpaceFeatures.Alarm, "Alarm", "The unit has its own alarm.", LocationVarKey.CortexFeatureAlarmName),
  unitTag(FacilityAndSpaceFeatures.LightInUnit, "Light in Unit", "The unit has interior lighting.", LocationVarKey.CortexFeatureLightInUnitName),
  unitTag(
    FacilityAndSpaceFeatures.HighCeiling,
    "High Ceiling",
    "The unit has a taller-than-standard ceiling.",
    LocationVarKey.CortexFeatureHighCeilingName
  ),
  unitTag(
    FacilityAndSpaceFeatures.Obstruction,
    "Obstruction",
    "The unit has a post, pipe, or other obstruction inside.",
    LocationVarKey.CortexFeatureObstructionName
  ),
  unitTag(FacilityAndSpaceFeatures.OddShaped, "Odd Shaped", "The unit is not a simple rectangle.", LocationVarKey.CortexFeatureOddShapedName),
  unitTag(FacilityAndSpaceFeatures.RollDoor, "Roll Door", "The unit has a roll-up door.", LocationVarKey.CortexFeatureRollDoorName),
  unitTag(
    FacilityAndSpaceFeatures.SmallRollDoor,
    "Small Roll Door",
    "The unit has a small roll-up door.",
    LocationVarKey.CortexFeatureSmallRollDoorName
  ),
  unitTag(
    FacilityAndSpaceFeatures.LargeRollDoor,
    "Large Roll Door",
    "The unit has a large roll-up door.",
    LocationVarKey.CortexFeatureLargeRollDoorName
  ),
  unitTag(FacilityAndSpaceFeatures.DoubleDoor, "Double Door", "The unit has double doors.", LocationVarKey.CortexFeatureDoubleDoorName),
  unitTag(FacilityAndSpaceFeatures.SwingDoor, "Swing Door", "The unit has a hinged swing door.", LocationVarKey.CortexFeatureSwingDoorName),
  unitTag(FacilityAndSpaceFeatures.GunStorage, "Gun Storage", "A space designated for firearm storage.", LocationVarKey.CortexFeatureGunStorageName),
  unitTag(FacilityAndSpaceFeatures.SafetyDepositBox, "Safety Deposit Box", "A safety deposit box.", LocationVarKey.CortexFeatureSafetyDepositBoxName),
  unitTag(
    FacilityAndSpaceFeatures.RinseStation,
    "Rinse Station",
    "A rinse station is available with this space.",
    LocationVarKey.CortexFeatureRinseStationName
  ),
  unitTag(
    FacilityAndSpaceFeatures.AirAndVacuumStation,
    "Air and Vacuum Station",
    "An air and vacuum station is available with this space.",
    LocationVarKey.CortexFeatureAirAndVacuumStationName
  ),

  // ─── Both levels ───────────────────────────────────────────────
  {
    key: FacilityAndSpaceFeatures.ClimateControl,
    label: "Climate-controlled units",
    scope: "both",
    category: "amenity",
    description: "Units maintain a stable temperature, suitable for sensitive items.",
    cortexVarKey: LocationVarKey.CortexFeatureClimateControl,
    nameVarKey: LocationVarKey.CortexFeatureClimateControlName,
    botWordingVarKey: LocationVarKey.CortexFeatureClimateControlBotWording,
    legacyHasKey: LocationVarKey.HasClimateControlledUnits,
    legacyNameKey: LocationVarKey.ClimateControlName,
    legacyTextKey: LocationVarKey.FeatureClimateControl1
  },
  {
    key: FacilityAndSpaceFeatures.Heated,
    label: "Heated units",
    scope: "both",
    category: "amenity",
    description: "Units with heating, useful in cold climates.",
    cortexVarKey: LocationVarKey.CortexFeatureHeatedUnits,
    nameVarKey: LocationVarKey.CortexFeatureHeatedUnitsName,
    botWordingVarKey: LocationVarKey.CortexFeatureHeatedUnitsBotWording,
    legacyHasKey: LocationVarKey.HasHeatedUnitsts
  },
  {
    key: FacilityAndSpaceFeatures.DriveUp,
    label: "Drive-up units",
    scope: "both",
    category: "amenity",
    description: "Drive a vehicle directly up to the unit door for loading.",
    cortexVarKey: LocationVarKey.CortexFeatureDriveUpAccess,
    nameVarKey: LocationVarKey.CortexFeatureDriveUpAccessName,
    botWordingVarKey: LocationVarKey.CortexFeatureDriveUpAccessBotWording,
    legacyHasKey: LocationVarKey.HasDriveUpUnits,
    legacyTextKey: LocationVarKey.LoadingAreaType
  },
  {
    key: FacilityAndSpaceFeatures.Elevators,
    label: "Elevators",
    scope: "both",
    category: "amenity",
    description: "Multi-story facility with elevator access; on a unit, the unit is reached by elevator.",
    cortexVarKey: LocationVarKey.CortexFeatureElevators,
    nameVarKey: LocationVarKey.CortexFeatureElevatorsName,
    botWordingVarKey: LocationVarKey.CortexFeatureElevatorsBotWording,
    legacyHasKey: LocationVarKey.HasElevators
  },
  {
    key: FacilityAndSpaceFeatures.Power,
    label: "Electricity in units",
    scope: "both",
    category: "amenity",
    description: "Units have electrical outlets available.",
    cortexVarKey: LocationVarKey.CortexFeatureElectricityInUnit,
    nameVarKey: LocationVarKey.CortexFeatureElectricityInUnitName,
    botWordingVarKey: LocationVarKey.CortexFeatureElectricityInUnitBotWording,
    legacyHasKey: LocationVarKey.HasElectricityInUnit
  },
  {
    key: FacilityAndSpaceFeatures.Locker,
    label: "Lockers",
    scope: "both",
    category: "amenity",
    description: "Small lockers available for short-term or low-volume storage.",
    cortexVarKey: LocationVarKey.CortexFeatureLockers,
    nameVarKey: LocationVarKey.CortexFeatureLockersName,
    botWordingVarKey: LocationVarKey.CortexFeatureLockersBotWording,
    legacyHasKey: LocationVarKey.HasLockers
  },
  {
    key: FacilityAndSpaceFeatures.OfficeSpace,
    label: "Office space",
    scope: "both",
    category: "amenity",
    description: "Rentable office space at the facility.",
    cortexVarKey: LocationVarKey.CortexFeatureOfficeSpace,
    nameVarKey: LocationVarKey.CortexFeatureOfficeSpaceName,
    botWordingVarKey: LocationVarKey.CortexFeatureOfficeSpaceBotWording,
    legacyHasKey: LocationVarKey.HasOfficeSpace
  },
  {
    key: FacilityAndSpaceFeatures.TwentyFourHourAccess,
    label: "24-hour access",
    scope: "both",
    category: "amenity",
    description: "Tenants have 24/7 access to their unit.",
    cortexVarKey: LocationVarKey.CortexFeatureTwentyFourHourAccess,
    nameVarKey: LocationVarKey.CortexFeatureTwentyFourHourAccessName,
    botWordingVarKey: LocationVarKey.CortexFeatureTwentyFourHourAccessBotWording,
    legacyHasKey: LocationVarKey.Has24HourAccess
  },
  {
    key: FacilityAndSpaceFeatures.RinseAndDumpStation,
    label: "Dump station",
    scope: "both",
    category: "amenity",
    description: "RV rinse and dump station on-site.",
    cortexVarKey: LocationVarKey.CortexFeatureDumpStation,
    nameVarKey: LocationVarKey.CortexFeatureDumpStationName,
    botWordingVarKey: LocationVarKey.CortexFeatureDumpStationBotWording,
    legacyHasKey: LocationVarKey.HasDumpStation
  },
  {
    key: FacilityAndSpaceFeatures.Parking,
    label: "Car & motorcycle storage",
    scope: "both",
    category: "storage_type",
    description:
      "Dedicated parking spaces for cars, motorcycles, and small vehicles — a plain parking space is an outdoor spot; covered and enclosed spaces are their own switches. Distinct from RV parking and boat parking. Standard indoor units are NOT car storage.",
    // Legacy `HasOutdoorVehicleStorage` also seeds this switch (see LEGACY_FLAG_ALIASES in Cortex.MigrationSpecs).
    cortexVarKey: LocationVarKey.CortexFeatureVehicleStorage,
    nameVarKey: LocationVarKey.CortexFeatureVehicleStorageName,
    botWordingVarKey: LocationVarKey.CortexFeatureVehicleStorageBotWording,
    legacyHasKey: LocationVarKey.HasVehicleStorage
  },
  {
    key: FacilityAndSpaceFeatures.RVParking,
    label: "RV parking",
    scope: "both",
    category: "storage_type",
    description: "RV and motorhome parking available.",
    cortexVarKey: LocationVarKey.CortexFeatureRVParking,
    nameVarKey: LocationVarKey.CortexFeatureRVParkingName,
    botWordingVarKey: LocationVarKey.CortexFeatureRVParkingBotWording,
    legacyHasKey: LocationVarKey.HasRVParking
  },
  {
    key: FacilityAndSpaceFeatures.BoatParking,
    label: "Boat storage",
    scope: "both",
    category: "storage_type",
    description: "Boat storage available.",
    cortexVarKey: LocationVarKey.CortexFeatureBoatStorage,
    nameVarKey: LocationVarKey.CortexFeatureBoatStorageName,
    botWordingVarKey: LocationVarKey.CortexFeatureBoatStorageBotWording,
    legacyHasKey: LocationVarKey.HasBoatStorage
  },
  {
    key: FacilityAndSpaceFeatures.WineStorage,
    label: "Wine storage",
    scope: "both",
    category: "storage_type",
    description: "Specialized temperature- and humidity-controlled wine storage.",
    cortexVarKey: LocationVarKey.CortexFeatureWineStorage,
    nameVarKey: LocationVarKey.CortexFeatureWineStorageName,
    botWordingVarKey: LocationVarKey.CortexFeatureWineStorageBotWording,
    legacyHasKey: LocationVarKey.HasWineStorage
  },
  {
    key: FacilityAndSpaceFeatures.CommercialWarehouse,
    label: "Business storage",
    scope: "both",
    category: "storage_type",
    description: "Storage product oriented toward business / commercial use.",
    cortexVarKey: LocationVarKey.CortexFeatureBusinessStorage,
    nameVarKey: LocationVarKey.CortexFeatureBusinessStorageName,
    botWordingVarKey: LocationVarKey.CortexFeatureBusinessStorageBotWording,
    legacyHasKey: LocationVarKey.HasBusinessStorage
  },
  {
    key: FacilityAndSpaceFeatures.ContainerUnit,
    label: "Portable units",
    scope: "both",
    category: "storage_type",
    description: "Portable / container storage units that can be delivered to the customer.",
    cortexVarKey: LocationVarKey.CortexFeaturePortableUnits,
    nameVarKey: LocationVarKey.CortexFeaturePortableUnitsName,
    botWordingVarKey: LocationVarKey.CortexFeaturePortableUnitsBotWording,
    legacyHasKey: LocationVarKey.HasPortableUnits
  },

  // ─── Facility compounds ────────────────────────────────────────
  {
    key: FacilityAndSpaceFeatures.CoveredRVParking,
    label: "Covered RV parking",
    scope: "facility",
    category: "storage_type",
    description: "Covered (roofed) RV parking.",
    cortexVarKey: LocationVarKey.CortexFeatureRVParkingCovered,
    nameVarKey: LocationVarKey.CortexFeatureRVParkingCoveredName,
    botWordingVarKey: LocationVarKey.CortexFeatureRVParkingCoveredBotWording,
    composedOf: { all: [FacilityAndSpaceFeatures.RVParking, FacilityAndSpaceFeatures.Covered] },
    legacyHasKey: LocationVarKey.HasRVParkingCovered
  },
  {
    key: FacilityAndSpaceFeatures.EnclosedRVParking,
    label: "Enclosed RV parking",
    scope: "facility",
    category: "storage_type",
    description: "Fully enclosed (indoor) RV parking.",
    cortexVarKey: LocationVarKey.CortexFeatureRVParkingEnclosed,
    nameVarKey: LocationVarKey.CortexFeatureRVParkingEnclosedName,
    botWordingVarKey: LocationVarKey.CortexFeatureRVParkingEnclosedBotWording,
    composedOf: { all: [FacilityAndSpaceFeatures.RVParking], any: [FacilityAndSpaceFeatures.Enclosed, FacilityAndSpaceFeatures.Inside] },
    legacyHasKey: LocationVarKey.HasRVParkingEnclosed
  },
  {
    key: FacilityAndSpaceFeatures.CoveredVehicleStorage,
    label: "Covered vehicle storage",
    scope: "facility",
    category: "storage_type",
    description: "Covered (roofed) vehicle storage, open on the sides.",
    cortexVarKey: LocationVarKey.CortexFeatureCoveredVehicleStorage,
    nameVarKey: LocationVarKey.CortexFeatureCoveredVehicleStorageName,
    botWordingVarKey: LocationVarKey.CortexFeatureCoveredVehicleStorageBotWording,
    composedOf: { all: [FacilityAndSpaceFeatures.Parking, FacilityAndSpaceFeatures.Covered] },
    legacyHasKey: LocationVarKey.HasCoveredVehicleStorage
  },
  {
    key: FacilityAndSpaceFeatures.IndoorVehicleStorage,
    label: "Indoor vehicle storage",
    scope: "facility",
    category: "storage_type",
    description: "Indoor (enclosed building) vehicle storage.",
    cortexVarKey: LocationVarKey.CortexFeatureIndoorVehicleStorage,
    nameVarKey: LocationVarKey.CortexFeatureIndoorVehicleStorageName,
    botWordingVarKey: LocationVarKey.CortexFeatureIndoorVehicleStorageBotWording,
    composedOf: { all: [FacilityAndSpaceFeatures.Parking], any: [FacilityAndSpaceFeatures.Enclosed, FacilityAndSpaceFeatures.Inside] },
    legacyHasKey: LocationVarKey.HasIndoorVehicleStorage
  },
  {
    key: FacilityAndSpaceFeatures.SmallVehicleStorage,
    label: "Small vehicle / motorcycle storage",
    scope: "facility",
    category: "storage_type",
    description: "Storage for motorcycles, ATVs, and other small vehicles.",
    cortexVarKey: LocationVarKey.CortexFeatureSmallVehicleStorage,
    nameVarKey: LocationVarKey.CortexFeatureSmallVehicleStorageName,
    botWordingVarKey: LocationVarKey.CortexFeatureSmallVehicleStorageBotWording,
    legacyHasKey: LocationVarKey.HasSmallVehicleStorage
  },

  // ─── Facility-only ─────────────────────────────────────────────
  {
    key: FacilityAndSpaceFeatures.AfterHoursAccess,
    label: "After-hours access",
    scope: "facility",
    category: "amenity",
    description: "Tenants can access their unit outside standard office hours.",
    cortexVarKey: LocationVarKey.CortexFeatureAfterHoursAccess,
    nameVarKey: LocationVarKey.CortexFeatureAfterHoursAccessName,
    botWordingVarKey: LocationVarKey.CortexFeatureAfterHoursAccessBotWording,
    legacyHasKey: LocationVarKey.HasAfterHoursAccess
  },
  {
    key: FacilityAndSpaceFeatures.HumidityControl,
    label: "Humidity-controlled units",
    scope: "facility",
    category: "amenity",
    description: "Units maintain controlled humidity in addition to temperature.",
    cortexVarKey: LocationVarKey.CortexFeatureHumidityControl,
    nameVarKey: LocationVarKey.CortexFeatureHumidityControlName,
    botWordingVarKey: LocationVarKey.CortexFeatureHumidityControlBotWording,
    legacyHasKey: LocationVarKey.HasHumidityControlledUnits
  },
  {
    key: FacilityAndSpaceFeatures.VideoSurveillance,
    label: "Video surveillance",
    scope: "facility",
    category: "amenity",
    description: "On-site video monitoring of common areas and access points.",
    cortexVarKey: LocationVarKey.CortexFeatureVideoSurveillance,
    nameVarKey: LocationVarKey.CortexFeatureVideoSurveillanceName,
    botWordingVarKey: LocationVarKey.CortexFeatureVideoSurveillanceBotWording,
    legacyHasKey: LocationVarKey.HasVideoSurveillanceveillance
  },
  {
    key: FacilityAndSpaceFeatures.ConferenceRoom,
    label: "Conference room",
    scope: "facility",
    category: "amenity",
    description: "On-site conference room available to tenants.",
    cortexVarKey: LocationVarKey.CortexFeatureConferenceRoom,
    nameVarKey: LocationVarKey.CortexFeatureConferenceRoomName,
    botWordingVarKey: LocationVarKey.CortexFeatureConferenceRoomBotWording,
    legacyHasKey: LocationVarKey.HasConferenceRoomnceRoom
  },
  {
    key: FacilityAndSpaceFeatures.OnSiteManager,
    label: "On-site manager",
    scope: "facility",
    category: "amenity",
    description: "A staffed on-site manager is present during business hours.",
    cortexVarKey: LocationVarKey.CortexFeatureOnSiteManager,
    nameVarKey: LocationVarKey.CortexFeatureOnSiteManagerName,
    botWordingVarKey: LocationVarKey.CortexFeatureOnSiteManagerBotWording,
    legacyHasKey: LocationVarKey.HasOnSiteManager
  },
  {
    key: FacilityAndSpaceFeatures.POMailbox,
    label: "PO mailbox rental",
    scope: "facility",
    category: "amenity",
    description: "PO box mailbox rental on-site.",
    cortexVarKey: LocationVarKey.CortexFeaturePOMailbox,
    nameVarKey: LocationVarKey.CortexFeaturePOMailboxName,
    botWordingVarKey: LocationVarKey.CortexFeaturePOMailboxBotWording,
    legacyHasKey: LocationVarKey.HasPOMailbox
  },
  {
    key: FacilityAndSpaceFeatures.Notary,
    label: "Notary services",
    scope: "facility",
    category: "amenity",
    description: "On-site notary services available.",
    cortexVarKey: LocationVarKey.CortexFeatureNotary,
    nameVarKey: LocationVarKey.CortexFeatureNotaryName,
    botWordingVarKey: LocationVarKey.CortexFeatureNotaryBotWording,
    legacyHasKey: LocationVarKey.HasNotary
  },
  {
    key: FacilityAndSpaceFeatures.DeliveriesAccepted,
    label: "Accepts deliveries",
    scope: "facility",
    category: "amenity",
    description: "The office will accept deliveries on a tenant's behalf.",
    cortexVarKey: LocationVarKey.CortexFeatureDeliveriesAccepted,
    nameVarKey: LocationVarKey.CortexFeatureDeliveriesAcceptedName,
    botWordingVarKey: LocationVarKey.CortexFeatureDeliveriesAcceptedBotWording,
    legacyHasKey: LocationVarKey.HasDeliveriesAccepted
  },
  {
    key: FacilityAndSpaceFeatures.Dumpsters,
    label: "Dumpsters",
    scope: "facility",
    category: "amenity",
    description: "On-site dumpsters for tenant use.",
    cortexVarKey: LocationVarKey.CortexFeatureDumpsters,
    nameVarKey: LocationVarKey.CortexFeatureDumpstersName,
    botWordingVarKey: LocationVarKey.CortexFeatureDumpstersBotWording,
    legacyHasKey: LocationVarKey.HasDumpsters
  },
  {
    key: FacilityAndSpaceFeatures.MovingTrucks,
    label: "Moving truck rental",
    scope: "facility",
    category: "service",
    description: "Moving trucks available to rent on-site.",
    cortexVarKey: LocationVarKey.CortexFeatureMovingTrucks,
    nameVarKey: LocationVarKey.CortexFeatureMovingTrucksName,
    botWordingVarKey: LocationVarKey.CortexFeatureMovingTrucksBotWording,
    legacyHasKey: LocationVarKey.HasMovingTrucks,
    legacyTextKey: LocationVarKey.TruckTypes
  },
  {
    key: FacilityAndSpaceFeatures.MovingServices,
    label: "Moving services",
    scope: "facility",
    category: "service",
    description: "Full-service moving help available through the facility.",
    cortexVarKey: LocationVarKey.CortexFeatureMovingServices,
    nameVarKey: LocationVarKey.CortexFeatureMovingServicesName,
    botWordingVarKey: LocationVarKey.CortexFeatureMovingServicesBotWording,
    legacyHasKey: LocationVarKey.HasMovingServices,
    pairedUrlKey: URLKey.MovingServices
  },
  {
    key: FacilityAndSpaceFeatures.PackingSupplies,
    label: "Packing supplies",
    scope: "facility",
    category: "service",
    description: "Boxes, tape, bubble wrap, and other packing supplies for sale.",
    cortexVarKey: LocationVarKey.CortexFeaturePackingSupplies,
    nameVarKey: LocationVarKey.CortexFeaturePackingSuppliesName,
    botWordingVarKey: LocationVarKey.CortexFeaturePackingSuppliesBotWording,
    legacyHasKey: LocationVarKey.HasPackingSupplies,
    pairedUrlKey: URLKey.PackingSupplies
  },
  {
    key: FacilityAndSpaceFeatures.Dollies,
    label: "Dollies / carts",
    scope: "facility",
    category: "service",
    description: "Dollies and carts available for tenant use.",
    cortexVarKey: LocationVarKey.CortexFeatureDollies,
    nameVarKey: LocationVarKey.CortexFeatureDolliesName,
    botWordingVarKey: LocationVarKey.CortexFeatureDolliesBotWording,
    legacyHasKey: LocationVarKey.HasDollies
  },
  {
    key: FacilityAndSpaceFeatures.Padlocks,
    label: "Padlocks",
    scope: "facility",
    category: "service",
    description: "Padlocks available for purchase on-site.",
    cortexVarKey: LocationVarKey.CortexFeaturePadlocks,
    nameVarKey: LocationVarKey.CortexFeaturePadlocksName,
    botWordingVarKey: LocationVarKey.CortexFeaturePadlocksBotWording,
    legacyHasKey: LocationVarKey.HasPadlocks
  },
  {
    key: FacilityAndSpaceFeatures.Propane,
    label: "Propane refills",
    scope: "facility",
    category: "service",
    description: "Propane tank refills offered at the facility.",
    cortexVarKey: LocationVarKey.CortexFeaturePropane,
    nameVarKey: LocationVarKey.CortexFeaturePropaneName,
    botWordingVarKey: LocationVarKey.CortexFeaturePropaneBotWording,
    legacyHasKey: LocationVarKey.HasPropane
  },
  {
    key: FacilityAndSpaceFeatures.RecycleProgram,
    label: "Recycling program",
    scope: "facility",
    category: "service",
    description: "Recycling drop-off available to tenants.",
    cortexVarKey: LocationVarKey.CortexFeatureRecycleProgram,
    nameVarKey: LocationVarKey.CortexFeatureRecycleProgramName,
    botWordingVarKey: LocationVarKey.CortexFeatureRecycleProgramBotWording,
    legacyHasKey: LocationVarKey.HasRecycleProgram
  },
  {
    key: FacilityAndSpaceFeatures.ShredProgram,
    label: "Document shredding",
    scope: "facility",
    category: "service",
    description: "Document shredding service available on-site.",
    cortexVarKey: LocationVarKey.CortexFeatureShredProgram,
    nameVarKey: LocationVarKey.CortexFeatureShredProgramName,
    botWordingVarKey: LocationVarKey.CortexFeatureShredProgramBotWording,
    legacyHasKey: LocationVarKey.HasShredProgram
  },
  {
    key: FacilityAndSpaceFeatures.Appointments,
    label: "Appointments / tours",
    scope: "facility",
    category: "service",
    description: "Tour appointments can be scheduled with the office.",
    cortexVarKey: LocationVarKey.CortexFeatureAppointments,
    nameVarKey: LocationVarKey.CortexFeatureAppointmentsName,
    botWordingVarKey: LocationVarKey.CortexFeatureAppointmentsBotWording,
    legacyHasKey: LocationVarKey.HasAppointments
  },
  {
    key: FacilityAndSpaceFeatures.TrailerStorage,
    label: "Trailer storage",
    scope: "facility",
    category: "storage_type",
    description: "Storage for trailers (utility, boat, equipment, etc.).",
    cortexVarKey: LocationVarKey.CortexFeatureTrailerStorage,
    nameVarKey: LocationVarKey.CortexFeatureTrailerStorageName,
    botWordingVarKey: LocationVarKey.CortexFeatureTrailerStorageBotWording,
    legacyHasKey: LocationVarKey.HasTrailerStorage
  }
];

/**
 * Hardcoded default on/off wording per operator-switch feature — the sentence the
 * bot reads when the operator hasn't turned on custom text. Unit-only tags have no
 * switch and therefore no entry; `resolveFeature` falls back to a label-derived
 * sentence for anything missing here. Reviewed 2026-06-22; re-keyed 2026-09-10.
 */
export const FEATURE_DEFAULT_TEXT: Partial<Record<FacilityAndSpaceFeatures, { on: string; off: string }>> = {
  // ─── Amenities ─────────────────────────────────────────────────
  [FacilityAndSpaceFeatures.AfterHoursAccess]: {
    on: "Tenants can access their unit outside standard office hours at this facility.",
    off: "This facility doesn't offer after-hours access — entry is limited to standard access hours."
  },
  [FacilityAndSpaceFeatures.TwentyFourHourAccess]: {
    on: "This facility offers 24-hour access to your unit.",
    off: "This facility doesn't offer 24-hour access — entry is limited to the posted access hours."
  },
  [FacilityAndSpaceFeatures.ClimateControl]: {
    on: "Climate-controlled units are available at this facility.",
    off: "This facility doesn't offer climate-controlled units."
  },
  [FacilityAndSpaceFeatures.HumidityControl]: {
    on: "Humidity-controlled units are available at this facility.",
    off: "This facility doesn't offer humidity-controlled units."
  },
  [FacilityAndSpaceFeatures.Heated]: {
    on: "Heated units are available at this facility.",
    off: "This facility doesn't offer heated units."
  },
  [FacilityAndSpaceFeatures.DriveUp]: {
    on: "Drive-up units are available at this facility.",
    off: "This facility doesn't offer drive-up units."
  },
  [FacilityAndSpaceFeatures.Elevators]: {
    on: "Elevators are available for reaching upper-floor units at this facility.",
    off: "This facility doesn't have elevators."
  },
  [FacilityAndSpaceFeatures.VideoSurveillance]: {
    on: "This facility has video surveillance.",
    off: "This facility doesn't have video surveillance."
  },
  [FacilityAndSpaceFeatures.Power]: {
    on: "Select units at this facility have electricity available.",
    off: "Units at this facility don't have electricity."
  },
  [FacilityAndSpaceFeatures.Locker]: {
    on: "Locker-sized units are available at this facility.",
    off: "This facility doesn't offer locker-sized units."
  },
  [FacilityAndSpaceFeatures.ConferenceRoom]: {
    on: "A conference room is available at this facility.",
    off: "This facility doesn't have a conference room."
  },
  [FacilityAndSpaceFeatures.OfficeSpace]: {
    on: "Office space is available to rent at this facility.",
    off: "This facility doesn't offer office space."
  },
  [FacilityAndSpaceFeatures.OnSiteManager]: {
    on: "This facility has an on-site manager.",
    off: "This facility doesn't have an on-site manager."
  },
  [FacilityAndSpaceFeatures.POMailbox]: {
    on: "Mailbox / PO box rentals are available at this facility.",
    off: "This facility doesn't offer mailbox rentals."
  },
  [FacilityAndSpaceFeatures.Notary]: {
    on: "Notary services are available at this facility.",
    off: "This facility doesn't offer notary services."
  },
  [FacilityAndSpaceFeatures.DeliveriesAccepted]: {
    on: "This facility accepts package deliveries on tenants' behalf.",
    off: "This facility doesn't accept deliveries on tenants' behalf."
  },
  [FacilityAndSpaceFeatures.RinseAndDumpStation]: {
    on: "A dump station is available at this facility.",
    off: "This facility doesn't have a dump station."
  },
  [FacilityAndSpaceFeatures.Dumpsters]: {
    on: "Dumpsters are available for tenant use at this facility.",
    off: "This facility doesn't provide dumpsters for tenant use."
  },

  // ─── Services ──────────────────────────────────────────────────
  [FacilityAndSpaceFeatures.MovingTrucks]: {
    on: "Moving truck rentals are available at this facility.",
    off: "This facility doesn't offer moving truck rentals."
  },
  [FacilityAndSpaceFeatures.MovingServices]: {
    on: "Moving services are available through this facility.",
    off: "This facility doesn't offer moving services."
  },
  [FacilityAndSpaceFeatures.PackingSupplies]: {
    on: "Packing supplies are available for purchase at this facility.",
    off: "This facility doesn't sell packing supplies."
  },
  [FacilityAndSpaceFeatures.Dollies]: {
    on: "Dollies and carts are available for tenant use at this facility.",
    off: "This facility doesn't provide dollies or carts."
  },
  [FacilityAndSpaceFeatures.Padlocks]: {
    on: "Padlocks are available for purchase at this facility.",
    off: "This facility doesn't sell padlocks."
  },
  [FacilityAndSpaceFeatures.Propane]: {
    on: "Propane refills are available at this facility.",
    off: "This facility doesn't offer propane refills."
  },
  [FacilityAndSpaceFeatures.RecycleProgram]: {
    on: "This facility has a recycling program.",
    off: "This facility doesn't have a recycling program."
  },
  [FacilityAndSpaceFeatures.ShredProgram]: {
    on: "Document shredding is available at this facility.",
    off: "This facility doesn't offer document shredding."
  },
  [FacilityAndSpaceFeatures.Appointments]: {
    on: "You can schedule an appointment or tour at this facility.",
    off: "This facility doesn't offer scheduled appointments or tours."
  },

  // ─── Storage Types ─────────────────────────────────────────────
  [FacilityAndSpaceFeatures.Parking]: {
    on: "Car and motorcycle storage is available at this facility.",
    off: "This facility doesn't offer car or motorcycle storage."
  },
  [FacilityAndSpaceFeatures.IndoorVehicleStorage]: {
    on: "Indoor vehicle storage is available at this facility.",
    off: "This facility doesn't offer indoor vehicle storage."
  },
  [FacilityAndSpaceFeatures.CoveredVehicleStorage]: {
    on: "Covered vehicle storage is available at this facility.",
    off: "This facility doesn't offer covered vehicle storage."
  },
  [FacilityAndSpaceFeatures.SmallVehicleStorage]: {
    on: "Small-vehicle and motorcycle storage is available at this facility.",
    off: "This facility doesn't offer small-vehicle or motorcycle storage."
  },
  [FacilityAndSpaceFeatures.RVParking]: {
    on: "RV parking is available at this facility.",
    off: "This facility doesn't offer RV parking."
  },
  [FacilityAndSpaceFeatures.CoveredRVParking]: {
    on: "Covered RV parking is available at this facility.",
    off: "This facility doesn't offer covered RV parking."
  },
  [FacilityAndSpaceFeatures.EnclosedRVParking]: {
    on: "Enclosed RV parking is available at this facility.",
    off: "This facility doesn't offer enclosed RV parking."
  },
  [FacilityAndSpaceFeatures.BoatParking]: {
    on: "Boat storage is available at this facility.",
    off: "This facility doesn't offer boat storage."
  },
  [FacilityAndSpaceFeatures.WineStorage]: {
    on: "Wine storage is available at this facility.",
    off: "This facility doesn't offer wine storage."
  },
  [FacilityAndSpaceFeatures.TrailerStorage]: {
    on: "Trailer storage is available at this facility.",
    off: "This facility doesn't offer trailer storage."
  },
  [FacilityAndSpaceFeatures.CommercialWarehouse]: {
    on: "Business storage is available at this facility.",
    off: "This facility doesn't offer business storage."
  },
  [FacilityAndSpaceFeatures.ContainerUnit]: {
    on: "Portable storage units are available at this facility.",
    off: "This facility doesn't offer portable storage units."
  }
};

/** Look up a feature definition by key. Returns undefined for unknown keys. */
export function findFeatureDef(key: FacilityAndSpaceFeatures): FacilityAndSpaceFeatureDef | undefined {
  return FACILITY_AND_SPACE_FEATURES.find((f) => f.key === key);
}
